import React, { useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { FiChevronDown } from 'react-icons/fi';
import { EmptyState } from '@/components/custom/empty-state';
import { Loader } from '@/components/custom/loader';
import EndScroll from '@/components/custom/end-scroll/end-scroll';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ClassificationModal } from './ClassificationModal';
import { formatDateTime, formatInteger, formatPercentage } from './formatters';
import usePlayfulTeacher from '@/hooks/usePlayfulTeacher';
import { PlayfulCardButton } from '@/components/custom/playful-card';

const COLUMN_WIDTHS = [260, 160, 100, 120, 160, 130, 160, 160];
const TABLE_WIDTH = COLUMN_WIDTHS.reduce((sum, width) => sum + width, 0);

// table-layout: fixed garante que as colunas não cresçam, mas não corta
// sozinho o conteúdo que estoura a célula (round-dg__cell/__cell-header não
// têm overflow:hidden em _components.data-grid.sass) — por isso o max-width
// em px explícito aqui, descontando o padding horizontal real da célula
// (--sm: th, por ser first-of-type, mantém os 20px do padding-left base +
// 6px de padding-right; td tem 6px dos dois lados) mais uma folga de 4px.
const TITLE_MAX_WIDTH = COLUMN_WIDTHS[0] - 20 - 6 - 4;
const SUBJECT_MAX_WIDTH = COLUMN_WIDTHS[1] - 6 - 6 - 4;

function ColumnGroup() {
  return (
    <colgroup>
      {COLUMN_WIDTHS.map((width, index) => (
        <col
          key={index}
          style={{ width }}
        />
      ))}
    </colgroup>
  );
}

function ChallengeAccordionItem({ challenge, isExpanded, onToggle, onSelectActivation }) {
  const playfulTeacher = usePlayfulTeacher();

  return (
    <div className={`accordion__item ${isExpanded ? 'active' : ''}`}>
      <div
        className="accordion__header"
        style={{ padding: 0, gap: 0 }}
        onClick={onToggle}
      >
        <div
          className="round-dg-wrapper"
          style={{ width: '100%' }}
        >
          <table style={{ width: '100%' }}>
            <ColumnGroup />
            <tbody>
              <tr className="round-dg__row">
                <th className="round-dg__cell-header round-dg__cell-header--sm round-dg__cell-header--row py-3 font-semibold text-primary-dark">
                  <p
                    className="truncate"
                    style={{ maxWidth: TITLE_MAX_WIDTH }}
                    title={challenge.title}
                  >
                    {challenge.title}
                  </p>
                </th>
                <td className="round-dg__cell round-dg__cell--sm py-3 text-center">
                  <Badge
                    variant={playfulTeacher ? 'info-soft' : 'default'}
                    size="sm"
                    style={{ maxWidth: SUBJECT_MAX_WIDTH }}
                    title={challenge.subject}
                  >
                    {challenge.subject}
                  </Badge>
                </td>
                <td className="round-dg__cell round-dg__cell--sm py-3">{challenge.createdByTeacher ? 'Sim' : 'Não'}</td>
                <td className="round-dg__cell round-dg__cell--sm py-3 text-center">{formatInteger(challenge.activations)}</td>
                <td className="round-dg__cell round-dg__cell--sm py-3 text-center">{formatInteger(challenge.avgParticipants)}</td>
                <td className="round-dg__cell round-dg__cell--sm py-3 text-center">{formatPercentage(challenge.avgAccuracyPercent)}</td>
                <td className="round-dg__cell round-dg__cell--sm py-3">{formatDateTime(challenge.lastActivationAt)}</td>
                <td className="round-dg__cell round-dg__cell--sm py-3 text-right">
                  <span className={playfulTeacher ? 'card__icon-wrapper !bg-transparent !p-0' : 'card__icon-wrapper card__icon-wrapper--sm'}>
                    <FiChevronDown className={playfulTeacher ? 'card__icon h-5 w-5' : 'card__icon'} />
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {isExpanded && (
        <div className="accordion__content p-0">
          <table style={{ width: '100%', fontSize: '14px' }}>
            <ColumnGroup />
            <tbody>
              {challenge.activationsList.map(activation => (
                <tr
                  key={activation.id}
                  className="round-dg__row"
                >
                  <th className="round-dg__cell-header py-3 pl-6 text-sm font-semibold text-primary-dark">{activation.label}</th>
                  <td className="round-dg__cell round-dg__cell--sm py-3">{formatDateTime(activation.date)}</td>
                  <td className="round-dg__cell round-dg__cell--sm py-3" />
                  <td className="round-dg__cell round-dg__cell--sm py-3">{`${formatInteger(activation.participants)} participantes`}</td>
                  <td className="round-dg__cell round-dg__cell--sm py-3" />
                  <td className="round-dg__cell round-dg__cell--sm py-3">{formatPercentage(activation.accuracyPercent)} de acerto</td>
                  <td className="round-dg__cell round-dg__cell--sm py-3" />
                  <td className="round-dg__cell round-dg__cell--sm py-3 text-right">
                    {playfulTeacher ? (
                      <PlayfulCardButton
                        className="h-9 w-fit px-2"
                        onClick={() => onSelectActivation(activation)}
                        variant="outline"
                      >
                        Ver resultados
                      </PlayfulCardButton>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="whitespace-nowrap"
                        onClick={() => onSelectActivation(activation)}
                      >
                        Ver resultados
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function ChallengesTable({ challenges = [], search, onSearchChange, hasMore, onLoadMore, initialLoading }) {
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [selectedActivation, setSelectedActivation] = useState(null);

  const toggleExpanded = challengeId => {
    setExpandedIds(current => {
      const next = new Set(current);
      if (next.has(challengeId)) {
        next.delete(challengeId);
      } else {
        next.add(challengeId);
      }
      return next;
    });
  };

  return (
    <div>
      <div className="report-widget__filter mb-4">
        <input
          className="report-form__control report-form__control--md"
          aria-label="Pesquisar"
          placeholder="Pesquisar"
          type="search"
          value={search}
          onChange={e => onSearchChange(e.target.value)}
        />
      </div>

      {initialLoading ? (
        <Loader />
      ) : challenges.length === 0 ? (
        <EmptyState
          type="data"
          text="Nenhum desafio encontrado"
        />
      ) : (
        <div className="round-dg-wrapper">
          <div style={{ minWidth: TABLE_WIDTH }}>
            <table style={{ width: '100%' }}>
              <ColumnGroup />
              <thead className="round-dg__header">
                <tr className="round-dg__row">
                  <th className="round-dg__cell-header round-dg__cell-header--sm">Desafio</th>
                  <th className="round-dg__cell-header round-dg__cell-header--sm text-center">Componente curricular</th>
                  <th className="round-dg__cell-header round-dg__cell-header--sm">Criador</th>
                  <th className="round-dg__cell-header round-dg__cell-header--sm text-center">Ativações</th>
                  <th className="round-dg__cell-header round-dg__cell-header--sm text-center">Média de participantes</th>
                  <th className="round-dg__cell-header round-dg__cell-header--sm text-center">% média de acerto</th>
                  <th className="round-dg__cell-header round-dg__cell-header--sm">Última ativação</th>
                  <th
                    className="round-dg__cell-header round-dg__cell-header--sm"
                    aria-hidden="true"
                  />
                </tr>
              </thead>
            </table>

            <InfiniteScroll
              dataLength={challenges.length}
              next={onLoadMore}
              hasMore={hasMore}
              loader={<Loader />}
              endMessage={<EndScroll />}
            >
              <div className="accordion mt-2">
                {challenges.map(challenge => (
                  <ChallengeAccordionItem
                    key={challenge.id}
                    challenge={challenge}
                    isExpanded={expandedIds.has(challenge.id)}
                    onToggle={() => toggleExpanded(challenge.id)}
                    onSelectActivation={activation => setSelectedActivation({ challengeTitle: challenge.title, activation })}
                  />
                ))}
              </div>
            </InfiniteScroll>
          </div>
        </div>
      )}

      <ClassificationModal
        show={!!selectedActivation}
        onClose={() => setSelectedActivation(null)}
        challengeTitle={selectedActivation?.challengeTitle}
        activation={selectedActivation?.activation}
      />
    </div>
  );
}
